/**
 * THE audio owner for the web app. Nothing else may construct an HTMLAudioElement,
 * an AudioContext, or call speechSynthesis.speak: `scripts/ci/check-audio-ownership.mjs`
 * will fail the build if it does.
 *
 * The bus exists because the correct path used to be optional. `lessonAudio.ts` was
 * already a single-owner module, and ten sites still bypassed it, three of them added
 * on one day. Exclusivity, the tutor pill, timeouts and error copy were each
 * reimplemented per activity or simply missing.
 */
import {
  audioOutcomeOf,
  audioReducer,
  IDLE_AUDIO_STATE,
  type AudioErrorReason,
  type AudioEvent,
  type AudioIntent,
  type AudioItem,
  type AudioOutcome,
  type AudioState,
} from "./audioContract";

export type { AudioOutcome } from "./audioContract";

export interface AudioRequest {
  source: { kind: "url"; url: string } | { kind: "buffer"; buffer: ArrayBuffer };
  intent: AudioIntent;
  /**
   * Shown on the tutor transport. An i18n KEY, not a resolved string: requests are
   * built in plain modules that cannot call `useTranslations`, so `TutorTransport`
   * resolves it with `t()` at render time.
   */
  label: string;
  rate?: number;
  preservesPitch?: boolean;
  /**
   * Default true. Set FALSE when replaying would change what the activity measures.
   * The transport is globally mounted, so its Replay is reachable over any clip:
   * speed-listening reports the fastest rung a learner followed on ONE hearing, and
   * spoken-numbers scores "caught it, first hearing", both of which a free replay
   * silently launders.
   */
  replayable?: boolean;
  /**
   * Default false. Set TRUE when the playback rate IS the exercise, so the transport
   * hides its speed control. Speed-listening plays identical bytes at every rung and
   * reports the rung as passed at that rate; letting the learner drop a 1.6x rung to
   * 0.75x from the pill makes the recorded result a fiction.
   */
  rateLocked?: boolean;
}

/**
 * A play() promise that never settles is the failure mode that reads as silence:
 * a media element stuck in NETWORK_LOADING never fires error and never resolves.
 * Bounding it converts silence into a visible error on the tutor.
 */
export const PLAY_START_TIMEOUT_MS = 10_000;

/** Sentinel so the catch can tell a deadline from a real player error. */
const TIMED_OUT = Symbol("audio-start-timeout");

function failureReason(error: unknown): AudioErrorReason {
  if (error === TIMED_OUT) return "timeout";
  // Read `name` structurally rather than via `instanceof Error`. Browsers reject a
  // blocked play() with a DOMException, and whether DOMException sits on the Error
  // prototype chain varies by environment - jsdom says no, browsers say yes. Keying
  // on the prototype makes a blocked autoplay get classified as a load failure in
  // some environments, which turns a benign non-event into a visible error.
  const name =
    typeof error === "object" && error !== null ? (error as { name?: unknown }).name : undefined;
  if (name === "NotAllowedError") return "blocked";
  return "load";
}

let state: AudioState = IDLE_AUDIO_STATE;
const listeners = new Set<(s: AudioState) => void>();

let element: HTMLAudioElement | null = null;
let objectUrl: string | null = null;
let lastRequest: AudioRequest | null = null;
/** Bumped on every request; events carrying a stale token are dropped. */
let token = 0;
let nextId = 0;
/**
 * Settles the in-flight playAudio promise WITH THE OUTCOME of its clip.
 *
 * Owned by `dispatch`, and by nothing else. Every event that takes a clip off the
 * bus settles the awaiting caller in the same breath as it moves the state machine,
 * so the outcome the caller sees cannot disagree with what the bus actually did.
 * teardown() used to settle this; it no longer does, because a teardown knows the
 * clip is going away but not why.
 *
 * INVARIANT that keeps that safe: every `teardown()` is followed, in the same
 * synchronous block, by a dispatch of `requested`, `stopped`, `failed` or `ended`.
 * Break it and a caller awaits forever, which is the exact failure this module
 * exists to remove.
 */
let settleFinished: ((outcome: AudioOutcome) => void) | null = null;
/**
 * Resolves when the media ELEMENT stops producing sound. Deliberately not the same
 * promise as `settleFinished`: the element stopping is what makes playAudio dispatch
 * `ended`, and dispatching `ended` is what settles the caller. Folding the two
 * together deadlocks, because the tail would be awaiting the promise it is itself on
 * the hook for resolving.
 */
let settleElementDone: (() => void) | null = null;
/** Bumped by every pause and every resume; an in-flight resume compares against it. */
let resumeAttempt = 0;

export function getAudioState(): AudioState {
  return state;
}

/**
 * Imperative, not part of the subscribed state: the passage reader polls this per
 * animation frame to sync word highlighting, and pushing it through subscribers
 * would re-render the tutor transport 60 times a second.
 */
export function getAudioCurrentTime(): number {
  return element?.currentTime ?? 0;
}

export function subscribeAudio(listener: (s: AudioState) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}

function dispatch(event: AudioEvent) {
  // Read the outcome off the EVENT, because the state the event produces has already
  // lost the distinction: `ended` and `stopped` both reduce to `idle`, and
  // `requested` has replaced the superseded caller's item with the one that
  // superseded it. Mutating this to derive the outcome from `state` after the reducer
  // has run fails the supersession and stop tests in audioBus.test.ts.
  //
  // The settle sits OUTSIDE the no-op guard below as DEFENCE IN DEPTH, not because a
  // path reaches it today - mutating it back inside the guard currently kills no
  // test, and that was measured rather than assumed. The events that reduce to the
  // same state object are `stopped` over an already-idle bus and `failed` with no
  // item, and no caller can be awaiting in either case: every terminal dispatch here
  // happens with the item still loaded. It is written this way because the cost is
  // one line and the failure it guards against - a terminal event that is a state
  // no-op stranding its caller forever - is the exact bug this module exists to
  // remove, and would be invisible until a learner hit it.
  const outcome = audioOutcomeOf(event);
  const next = audioReducer(state, event);
  if (next !== state) {
    state = next;
    for (const listener of listeners) listener(state);
  }
  if (outcome !== null) settle(outcome);
}

function settle(outcome: AudioOutcome) {
  const resolve = settleFinished;
  settleFinished = null;
  resolve?.(outcome);
}

function teardown() {
  if (element) {
    const previous = element;
    element = null;
    previous.onended = null;
    previous.onerror = null;
    previous.onpause = null;
    previous.pause();
  }
  // The element is gone, so its "it stopped sounding" signal can never arrive.
  // Whoever tore it down dispatches the terminal event, and that is what settles
  // the awaiting caller - see the invariant on `settleFinished`.
  settleElementDone = null;
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  }
  if (typeof window !== "undefined" && window.speechSynthesis?.speaking) {
    window.speechSynthesis.cancel();
  }
}

export function stopAudio(): void {
  token += 1;
  teardown();
  dispatch({ type: "stopped" });
}

/**
 * Resolves with WHY the clip left the bus. It still never rejects, and a caller that
 * ignores the value is still correct - this widens the contract rather than changing
 * it - but every caller whose next step is only valid after a clip was heard end to
 * end should be gating on `"ended"`.
 */
export async function playAudio(request: AudioRequest): Promise<AudioOutcome> {
  token += 1;
  const mine = token;
  teardown();

  const item: AudioItem = {
    id: String((nextId += 1)),
    intent: request.intent,
    label: request.label,
    replayable: request.replayable ?? true,
    rateLocked: request.rateLocked ?? false,
  };
  lastRequest = request;
  const rate = request.rate ?? 1;
  // Settles the PREVIOUS caller, with "superseded". `settleFinished` still holds its
  // resolver at this point; this call's own is installed further down, after the
  // element exists. Reordering those two hands the previous caller's outcome to the
  // new one and strands the old one.
  dispatch({ type: "requested", item, rate });

  const src =
    request.source.kind === "url"
      ? request.source.url
      : (objectUrl = URL.createObjectURL(
          new Blob([request.source.buffer], { type: "audio/mpeg" }),
        ));

  const audio = new Audio(src);
  audio.playbackRate = rate;
  audio.preservesPitch = request.preservesPitch ?? true;
  element = audio;

  // What THIS caller awaits. Resolved only from `dispatch`, off the event that takes
  // the clip off the bus.
  const outcome = new Promise<AudioOutcome>((resolve) => {
    settleFinished = resolve;
  });

  // Internal, and separate on purpose: the element going quiet is not itself an
  // outcome, it is the trigger for dispatching one.
  //
  // WHY the element stopped is carried through, because "it reached the end" and
  // "it died partway" are different clips as far as the learner is concerned. A
  // mid-playback media error used to arrive here as a plain resolve and dispatch
  // `ended`, so a clip that cut out halfway counted as a full hearing: speed
  // listening revealed its questions and spoken-numbers started its answer clock,
  // both off audio nobody finished hearing.
  const elementDone = new Promise<"ended" | "error">((resolve) => {
    settleElementDone = () => resolve("ended");
    audio.onended = () => resolve("ended");
    audio.onerror = () => resolve("error");
    audio.onpause = () => resolve("ended");
  });

  // The deadline must REJECT THE AWAITED EXPRESSION, not merely fire a side effect.
  // A stalled media element's play() promise never settles, so a timeout that only
  // tears down leaves this function suspended on play() forever - reintroducing the
  // never-settling promise the timeout exists to prevent.
  let fireTimeout: () => void = () => {};
  const startDeadline = new Promise<never>((_, reject) => {
    fireTimeout = () => reject(TIMED_OUT);
  });
  const timeout = setTimeout(fireTimeout, PLAY_START_TIMEOUT_MS);

  try {
    await Promise.race([audio.play(), startDeadline]);
    clearTimeout(timeout);
    // Superseded while play() was pending. Whatever superseded this clip already
    // settled `outcome`; returning it hands the caller that reason.
    if (mine !== token) return outcome;
    dispatch({ type: "started" });
    if (rate !== 1) dispatch({ type: "rate-changed", rate });
  } catch (error) {
    clearTimeout(timeout);
    if (mine !== token) return outcome;
    // Bump the token BEFORE tearing down: pause() rejects the still-pending play()
    // promise with AbortError, and without this bump that late rejection would pass
    // the guard above and overwrite a "timeout" reason with "load".
    token += 1;
    teardown();
    dispatch({ type: "failed", reason: failureReason(error) });
    return outcome;
  }

  // Chained, NOT awaited. A superseded or stopped clip has its element torn down
  // with every handler detached, so `elementDone` never resolves for it; suspending
  // this function on it would mean the returned promise never settled either, which
  // is the hang the bus exists to prevent. This branch covers one case only - the
  // clip running out on its own - and `outcome` is settled by whoever ends it.
  void elementDone.then((how) => {
    if (mine !== token) return;
    teardown();
    if (how === "error") {
      dispatch({ type: "failed", reason: "load" });
      return;
    }
    dispatch({ type: "ended" });
  });

  return outcome;
}

export function pauseAudio(): void {
  if (state.status !== "playing" || !element) return;
  // Invalidate any in-flight resume attempt. A pause landing while a resume's
  // play() is still pending rejects that promise with AbortError, and without
  // this the resume would read its own supersession as a genuine failure.
  resumeAttempt += 1;
  // Suppress the onpause resolver so a pause is not read as the clip ending.
  //
  // The awaiting caller therefore stays suspended for as long as the clip is paused,
  // and that is deliberate rather than an oversight. Settling here would mean "this
  // clip is done" to every caller - the passage reader would drop its word
  // highlighting and its ownership of a clip that is still on the bus and still
  // resumable - and playAudio's tail would tear the element down, so Resume would
  // have nothing to resume. Everything that ends the pause does settle it: resume
  // then end, stop, a new request, replay, or a media error. `pauseAudio` is only
  // reachable from the globally mounted tutor transport, which renders Stop beside
  // Resume for the whole paused state, so the suspension is always one visible
  // control away from ending.
  element.onpause = null;
  element.pause();
  dispatch({ type: "paused" });
}

export function resumeAudio(): void {
  if (state.status !== "paused" || !element) return;
  const resumed = element;
  // The element reference alone cannot express "this same element was paused
  // again while my play() was still pending", which is what a quick
  // pause-resume-pause produces.
  const attempt = (resumeAttempt += 1);
  // Restore the resolver pause() nulled, or a later browser-initiated pause
  // (audio-focus loss, headphones unplugged) would never settle `finished`.
  resumed.onpause = () => settleElementDone?.();
  void resumed.play().catch((error) => {
    // Benign if anything superseded this attempt: another pause, a new clip, or a
    // stop. Only an attempt that is still current represents a real failure.
    if (element !== resumed || attempt !== resumeAttempt) return;
    // Bump the token BEFORE tearing down. The original playAudio() call still has a
    // live `elementDone` continuation on this element, and pause() inside teardown()
    // can resolve it; without the bump that tail sees mine === token, runs, and
    // dispatches "ended" right after this "failed". That clobbers the error state
    // back to idle AND hands the awaiting caller "ended" for a clip that failed -
    // the tutor pill goes silent instead of showing the error it should.
    token += 1;
    teardown();
    dispatch({ type: "failed", reason: failureReason(error) });
  });
  dispatch({ type: "resumed" });
}

/** Null when there is nothing to replay; otherwise the replay's own outcome. */
export async function replayAudio(): Promise<AudioOutcome | null> {
  if (!lastRequest) return null;
  return playAudio(lastRequest);
}

export function setAudioRate(rate: number): void {
  if (element) element.playbackRate = rate;
  dispatch({ type: "rate-changed", rate });
}
